<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import debounce from 'lodash/debounce';
import useDashboardStore from '@/stores/Dashboard/dashboard-store';
import { DashboardWidget, ApiResponse, Serie, Status } from '@/interfaces/Dashboard';
import ConnxStatusCard from '@/components/Atoms/ConnxStatusCard.vue';
import ConnxDashboardWidgetNoData from './ConnxDashboardWidgetNoData.vue';

const props = defineProps<{ height: number; type: string; widget: DashboardWidget }>();

const isLoading = ref(true);
const total = ref<{ label: string; value: number } | null>(null);
const licenceStats = ref<
  {
    label: string;
    value: number;
    status: Status;
    ratio: { value: number; total: number };
    percentage: string;
  }[]
>([]);

const dashboardStore = useDashboardStore();

const fetchData = async () => {
  try {
    isLoading.value = true;

    const response = (await dashboardStore.fetchDataSource(props.widget.dataSourceGuid, {
      widgetGuid: props.widget.dashboardTabWidgetGuid,
    })) as ApiResponse<Serie[]>;

    const apiData = response?.data;

    if (Array.isArray(apiData) && apiData.length > 0) {
      const totalItem = apiData[0];
      const rawTotal = totalItem?.data ?? 0;
      const normalizedTotal = Array.isArray(rawTotal) ? Number(rawTotal[0] ?? 0) : Number(rawTotal ?? 0);
      const totalValue = Number.isNaN(normalizedTotal) ? 0 : normalizedTotal;

      total.value = {
        label: totalItem?.name ?? 'Total',
        value: totalValue,
      };

      const statusItems = apiData.slice(1);

      licenceStats.value = statusItems.map((item, index) => {
        const colors: Status[] = ['success', 'warning', 'danger', 'danger'];
        const statusColor = colors[index % colors.length];

        const rawValue = item.data;
        const normalizedValue = Array.isArray(rawValue) ? Number(rawValue[0] ?? 0) : Number(rawValue ?? 0);
        const valueNumber = Number.isNaN(normalizedValue) ? 0 : normalizedValue;

        const percentage = totalValue > 0 ? `${((valueNumber / totalValue) * 100).toFixed(0)}%` : '0%';

        const mapped = {
          label: item.name,
          value: valueNumber,
          status: statusColor,
          ratio: { value: valueNumber, total: totalValue },
          percentage,
        };
        return mapped;
      });
    } else {
      console.warn('No valid data in API response.');
      total.value = null;
      licenceStats.value = [];
    }
  } catch (err) {
    console.error('Error fetching licence data:', err);
    total.value = null;
    licenceStats.value = [];
  } finally {
    isLoading.value = false;
  }
};

const debouncedFetchData = debounce(async () => {
  await fetchData();
}, 600);

watch(
  () => {
    const p = props.widget.defaultJsonProperties?.parameters;
    const filterValues = Array.isArray(p?.filterBy) ? p.filterBy.map((f) => f.value) : [];
    return { filterByValues: filterValues };
  },
  async () => {
    await debouncedFetchData();
  },
  { deep: false }
);

onMounted(() => {
  fetchData();
});
</script>

<template>
  <!-- Main Content -->
  <template v-if="!isLoading">
    <div class="ScrollBar ScrollBar--vertical ScrollBar--horizontal-hidden">
      <div
      :style="{ height: `${height}px` }"
      class="tw-w-full tw-h-full tw-pl-0 tw-pr-4 tw-pb-4 tw-pt-4 tw-flex tw-flex-col tw-gap-4"
    >
        <!-- Total Section -->
       <div
        v-if="total"
        class="tw-border-2 tw-font-bold tw-text-xl tw-border-solid tw-rounded-full tw-px-6 tw-py-2 tw-my-1 tw-text-center tw-border-connx-grey tw-flex-none tw-bg-white tw-w-full"
      >
          {{ total.label }}: {{ Math.round(total.value) }}
        </div>

        <!-- Cards Grid -->
        <div
          v-if="licenceStats.length"
          class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4 tw-flex-1  tw-rounded-xl tw-bg-connx-grey-tint-95/40 "
        >
          <ConnxStatusCard
            v-for="(item, index) in licenceStats"
            :key="index"
            :title="item.value"
            :status="item.status"
            :badge-text="item.label"
            :label-text="item.percentage"
            :ratio="item.ratio"
            :progressHeight="5"
            :progressrounded="false"
            class="tw-h-full tw-flex tw-flex-col tw-justify-center"
          />
        </div>

        <!-- Empty state -->
        <ConnxDashboardWidgetNoData v-if="!licenceStats.length" />
      </div>
    </div>
  </template>
</template>

